import type { AnswerClosure } from "../utils/answerClosure";
import type { ReadyQuestionsConfig } from "../utils/readyQuestions";

export const enum RetrievalMode {
    Hybrid = "hybrid",
    Vectors = "vectors",
    Text = "text"
}

export const enum GPT4VInput {
    TextAndImages = "textAndImages",
    Images = "images",
    Texts = "texts"
}

export const enum VectorFieldOptions {
    Embedding = "embedding",
    ImageEmbedding = "imageEmbedding",
    Both = "both"
}

export const enum ModelType {
    GPT4O = "gpt-4o",
    GPT5Mini = "gpt-5-mini",
    GPT5Nano = "gpt-5-nano",
    GPT51Chat = "gpt-5.1-chat",
    Gemini25Flash = "gemini-2.5-flash",
    Gemini25FlashLite = "gemini-2.5-flash-lite",
    Gemini25Pro = "gemini-2.5-pro"
}

export const enum ReasoningEffort {
    Low = "low",
    Medium = "medium"
}

export type PriceSource = "web" | "store";
export type SiteId = "PUBLIC_GR" | "PUBLIC_CY";

export const SITE_ID_LABELS: Record<SiteId, string> = {
    PUBLIC_GR: "Public.gr",
    PUBLIC_CY: "Public.cy"
};

/** Default siteId → numeric n8n siteId mapping. Override via VITE_SITE_ID_PUBLIC_GR / VITE_SITE_ID_PUBLIC_CY env vars. */
export const SITE_ID_MAP: Record<SiteId, string> = {
    PUBLIC_GR: (import.meta as any).env?.VITE_SITE_ID_PUBLIC_GR || "90001",
    PUBLIC_CY: (import.meta as any).env?.VITE_SITE_ID_PUBLIC_CY || "800001"
};

/** Convert a SiteId to its numeric n8n siteId string. */
export function toN8nSiteId(siteId: SiteId): string {
    return SITE_ID_MAP[siteId] || SITE_ID_MAP.PUBLIC_GR;
}

/**
 * Normalize a host/n8n site identifier to the widget's internal SiteId.
 *
 * Native hosts use the public contract aliases "GR" / "CY", while the web app
 * and gateway also use "PUBLIC_GR" / "PUBLIC_CY" and numeric n8n IDs.
 */
export function fromN8nSiteId(n8nId: string): SiteId {
    const normalized = (n8nId || "").trim().toUpperCase();
    if (normalized === "GR" || normalized === "PUBLIC_GR") return "PUBLIC_GR";
    if (normalized === "CY" || normalized === "PUBLIC_CY") return "PUBLIC_CY";

    for (const [s, id] of Object.entries(SITE_ID_MAP)) {
        if (id === normalized) return s as SiteId;
    }
    return "PUBLIC_GR";
}

export type ChatAppRequestOverrides = {
    retrieval_mode?: RetrievalMode;
    semantic_ranker?: boolean;
    semantic_captions?: boolean;
    exclude_category?: string;
    top?: number;
    temperature?: number;
    prompt_template?: string;
    prompt_template_prefix?: string;
    prompt_template_suffix?: string;
    suggest_followup_questions?: boolean;
    use_oid_security_filter?: boolean;
    use_groups_security_filter?: boolean;
    use_gpt4v?: boolean;
    gpt4v_input?: GPT4VInput;
    vector_fields: VectorFieldOptions[];
    intent_model?: string;
    response_model?: string;
    reasoning_effort?: string;
    price_source?: PriceSource;
    site_id?: SiteId;
};

export type ResponseMessage = {
    content: string;
    role: string;
};

export type Thoughts = {
    title: string;
    description: any; // It can be any output from the api
    props?: { [key: string]: string };
};

export type ResponseContext = {
    data_points: string[];
    followup_questions: string[] | null;
    thoughts: Thoughts[];
    // ENG-027: the agent backend's verdict on whether this answer closes the question
    // (end frame / non-streaming context). Absent for n8n — the chat then classifies the text.
    closure?: AnswerClosure;
};

export type ResponseChoice = {
    index: number;
    message: ResponseMessage;
    context: ResponseContext;
    session_state: any;
};

export type ChatAppResponseOrError = {
    choices?: ResponseChoice[];
    error?: string;
};

export type TokenUsage = {
    completion_tokens: number;
    prompt_tokens: number;
    total_tokens: number;
    completion_tokens_details?: {
        accepted_prediction_tokens?: number;
        audio_tokens?: number;
        reasoning_tokens?: number;
        rejected_prediction_tokens?: number;
    };
    prompt_tokens_details?: {
        audio_tokens?: number;
        cached_tokens?: number;
    };
};

export type ChatAppResponse = {
    choices: ResponseChoice[];
    usage?: TokenUsage;
};

export type ChatAppRequestContext = {
    overrides?: ChatAppRequestOverrides;
};

export type ChatAppRequest = {
    messages: ResponseMessage[];
    context?: ChatAppRequestContext;
    stream?: boolean;
    user_id?: string;
    user_name?: string;
    user_email?: string;
    client_channel?: "widget" | "genie_widget" | "app";
    // Optional host-page context (factual), e.g. "The user is currently on the
    // host site's product page for SKU 12345." Set when the widget is embedded
    // on a product page; omitted otherwise.
    host_context?: string;
    // ENG-011: per-message input-mode analytics. input_mode defaults to "typed" on the
    // gateway when omitted (e.g. older clients). voice_edited/speech_backend only apply
    // when input_mode is "voice".
    input_mode?: "voice" | "typed";
    voice_edited?: boolean;
    // ENG-034: a plain string, not a closed union — ChatWidget.submitText({inputMode:"voice"})
    // can carry a host-supplied backend id (default "genie_native"), not just this app's own
    // "browser"/"azure" recognizers. The gateway validates it against its own allow-list
    // (chat.py _VALID_SPEECH_BACKENDS) and drops anything it does not recognize.
    speech_backend?: string;
    // ENG-014: true when the voice message was sent by the auto-send timer (voice only).
    auto_submitted?: boolean;
    // ENG-054: the conversation thread the message belongs to (tickets/.../product-threads
    // README "Κοινό συμβόλαιο"). Missing or invalid means "general" on the gateway.
    conversation_thread?: "product" | "general";
    // ENG-054: true only when the message was a click on a product intro's suggested question.
    intro_suggestion?: true;
    // ENG-067: which ready question, "<group id>.<question id>" (e.g. "phones.camera"); only with intro_suggestion.
    intro_suggestion_id?: string;
    session_state: any;
};

export type Config = {
    showGPT4VOptions: boolean;
    showSemanticRankerOption: boolean;
    showVectorOption: boolean;
};

export type ChatBackend = "n8n" | "agent";

export type AgentConfig = {
    backendBySite: Record<string, ChatBackend>;
    model: string; // "default" = gateway env primary chat deployment
    temperature: number | null;
};

// ENG-015: the public switches of GET /n8n-config's speech block (the rest is admin-only UI data).
export type SpeechPublicConfig = {
    backend?: "auto" | "browser" | "azure";
    enabled?: boolean;
    enabledByChannel?: Partial<Record<"app" | "widget" | "genie_widget", boolean>>;
    // ENG-069: the value in force per timeout; the widget reads only autoSendDelayMs (the auto-send countdown).
    timeouts?: { autoSendDelayMs?: { value?: number } };
};

export type N8NConfig = {
    regions: {
        PUBLIC_GR: { enabled: boolean; chatUrl?: string };
        PUBLIC_CY: { enabled: boolean; chatUrl?: string };
    };
    appTitle?: string;
    agent?: AgentConfig;
    speech?: SpeechPublicConfig;
    // ENG-067: the product intro's ready questions per product type (public; the seed until an admin saves).
    readyQuestions?: ReadyQuestionsConfig;
};
